<kno2-dialog-body [isLoading]="isLoading()">
    @if (error()) {
        <div class="alert alert-danger mb-3">
            {{ error() }}
        </div>
    }

    <form
        [formGroup]="form"
        class="form-horizontal"
        role="form"
        novalidate
        (ngSubmit)="saveChanges()">
        @if (idProofProcessStatusMessage()) {
            <div class="alert alert-info mb-4">
                <p>{{ idProofProcessStatusMessage() }}</p>
                @if (idProofProcessStatusMessageClearAdditionalDetail()) {
                    <p>
                        Link to verification:
                        <a
                            [href]="idProofProcessStatusMessageClearAdditionalDetailLink()"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-primary text-decoration-underline">
                            {{ user()?.idProofClearVerificationId }}
                        </a>
                        <a
                            class="ms-3"
                            role="button"
                            style="cursor: pointer"
                            (click)="resetFullIdProofing(); $event.preventDefault()">
                            @if (isResettingFullIdProofing()) {
                                <span class="spinner-border spinner-border-sm me-2"></span>
                            } @else {
                                <i
                                    class="fa fa-trash"
                                    [kno2Popover]="resetFullIdProofingTooltip"></i>
                            }
                        </a>
                        @if (canResyncClearIdProofing()) {
                            <a
                                class="ms-2"
                                role="button"
                                style="cursor: pointer"
                                (click)="resyncClearIdProofing(); $event.preventDefault()">
                                @if (isResyncingClearIdProofing()) {
                                    <span class="spinner-border spinner-border-sm me-2"></span>
                                } @else {
                                    <i
                                        class="fa fa-refresh"
                                        [kno2Popover]="resyncClearIdProofingTooltip"></i>
                                }
                            </a>
                        }
                    </p>
                }
            </div>
        }

        <div class="row mb-3">
            <label
                for="idProof"
                class="col-sm-4 col-form-label text-end">
                ID Proofing Status
            </label>
            <div class="col-sm-3">
                <kno2-form-select
                    [control]="form.get('passed')"
                    [options]="statusOptions()"
                    [placeholder]="'Select Status'"
                    [hasError]="form.get('passed')?.invalid && form.get('passed')?.touched" />
                <kno2-form-input-error
                    [control]="form.controls.passed"
                    showErrorOn="touched" />
            </div>
            <span
                class="ps-2"
                [kno2Popover]="statusTooltip">
                <i class="fa fa-info-circle box-info border-0 me-0"></i>
            </span>
        </div>

        @if (user()?.idProofFileName) {
            <div class="row mb-3">
                <label class="col-sm-4 col-form-label text-end">Identification Uploaded</label>
                <div class="col-sm-3">
                    <button
                        type="button"
                        class="btn btn-sm btn-primary w-100"
                        [disabled]="isAnyActionInProgress()"
                        (click)="downloadUserIdProof()">
                        @if (isDownloading()) {
                            <span class="spinner-border spinner-border-sm me-2"></span>
                        }
                        View Document
                    </button>
                </div>
                @if (!user()?.idProofed) {
                    <div class="col-sm-3 p-0">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            [disabled]="isAnyActionInProgress()"
                            (click)="resetManualIdProofing()">
                            @if (isResettingManualId()) {
                                <span class="spinner-border spinner-border-sm me-2"></span>
                            } @else {
                                <i
                                    class="fa fa-trash"
                                    [kno2Popover]="resetManualIdTooltip"></i>
                            }
                        </button>
                    </div>
                }
            </div>
        }
    </form>

    @if (canEnterClearVerificationId()) {
        <div class="mt-2">
            @if (!isClearEntryOpen()) {
                <a
                    role="button"
                    class="d-inline-flex align-items-center text-primary text-decoration-underline"
                    style="cursor: pointer"
                    [kno2Popover]="enterClearVerificationIdTooltip"
                    (click)="openClearVerificationEntry()">
                    <i class="fa fa-search me-2"></i>
                    Manually link a CLEAR verification ID
                </a>
            } @else {
                <div class="card border-light bg-light">
                    <div class="card-body">
                        <div class="d-flex align-items-center mb-2">
                            <i class="fa fa-search text-primary me-2"></i>
                            <span class="fw-bold">Link CLEAR Verification</span>
                            <a
                                role="button"
                                class="ms-auto text-muted"
                                style="cursor: pointer"
                                (click)="closeClearVerificationEntry()">
                                <i class="fa fa-times"></i>
                            </a>
                        </div>

                        @if (!clearPreview()) {
                            <p class="text-muted small mb-3">
                                Use when a user completed CLEAR but never returned to finish. Enter the verification ID to retrieve and link their verification.
                            </p>
                            <div class="d-flex align-items-start">
                                <div class="flex-grow-1 me-2">
                                    <kno2-form-input
                                        [control]="clearVerificationIdControl"
                                        placeholder="Enter CLEAR verification ID"
                                        [hasError]="clearVerificationIdControl.invalid && clearVerificationIdControl.touched" />
                                    <kno2-form-input-error
                                        [control]="clearVerificationIdControl"
                                        showErrorOn="touched" />
                                </div>
                                <button
                                    type="button"
                                    class="btn btn-primary text-nowrap"
                                    [disabled]="isAnyActionInProgress()"
                                    (click)="lookupClearVerification()">
                                    @if (isPreviewingClearVerification()) {
                                        <span class="spinner-border spinner-border-sm me-2"></span>
                                    } @else {
                                        <i class="fa fa-search me-2"></i>
                                    }
                                    Look up
                                </button>
                            </div>
                        } @else {
                            <div class="d-flex align-items-center mb-3">
                                <i class="fa fa-check-circle text-success fa-2x me-3"></i>
                                <div>
                                    <div class="text-muted small text-uppercase">Verification found</div>
                                    <div class="fw-bold">{{ clearPreview().firstName }} {{ clearPreview().lastName }}</div>
                                    @if (clearPreview().dateOfBirth) {
                                        <div class="small text-muted">DOB {{ clearPreview().dateOfBirth | date: "MM/dd/yyyy" }}</div>
                                    }
                                </div>
                            </div>
                            <p class="mb-3">
                                Link this CLEAR verification to
                                <strong>{{ user().firstName }} {{ user().lastName }}</strong>
                                ?
                            </p>
                            <div class="d-flex align-items-center">
                                <button
                                    type="button"
                                    class="btn btn-primary me-3"
                                    [disabled]="isAnyActionInProgress()"
                                    (click)="linkClearVerification()">
                                    @if (isLinkingClearVerification()) {
                                        <span class="spinner-border spinner-border-sm me-2"></span>
                                    }
                                    Link verification
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-default"
                                    [disabled]="isAnyActionInProgress()"
                                    (click)="resetClearVerificationLookup()">
                                    Try a different ID
                                </button>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>
    }
</kno2-dialog-body>

<kno2-dialog-footer>
    <button
        type="button"
        class="btn btn-default"
        [disabled]="isAnyActionInProgress()"
        (click)="cancel()">
        Cancel
    </button>
    @if (canChangeIdProofStatus()) {
        <button
            type="button"
            class="btn btn-primary"
            [disabled]="!user() || isAnyActionInProgress()"
            (click)="saveChanges()">
            @if (isSubmitting()) {
                <span class="spinner-border spinner-border-sm me-1"></span>
            }
            Save Changes
        </button>
    }
</kno2-dialog-footer>
